import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChatClientService } from 'stream-chat-angular';
import {
  EMPTY,
  Observable,
  ReplaySubject,
  Subject,
  catchError,
  distinctUntilChanged,
  filter,
  from,
  fromEvent,
  map,
  merge,
  startWith,
  switchMap,
} from 'rxjs';

interface ChatUser {
  id: string;
  total_unread_count?: number;
}

/**
 * Unread chat messages of the connected user, for the sidebar and the app icon
 * badge.
 *
 * Stream only pushes changes over the WebSocket. Events missed while the
 * socket was down (app in the background, phone asleep) are not replayed, so
 * the count is fetched again from Stream when the connection is recovered and
 * when the app becomes visible; a newer trigger replaces an open request. Counts come from the raw events rather than
 * `ChatClientService.user$`, which drops an event whose count equals its own
 * possibly outdated value.
 */
@Injectable({ providedIn: 'root' })
export class ChatUnreadService {
  private readonly chat = inject(ChatClientService);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly countSubject = new ReplaySubject<number>(1);

  /** Emits once the chat is connected, after every change and after every resync. */
  readonly count$: Observable<number> = this.countSubject.asObservable();

  constructor() {
    (this.chat.user$ as Observable<ChatUser | null | undefined>)
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        switchMap(user => (user ? this.track(user) : EMPTY)),
        takeUntilDestroyed()
      )
      .subscribe(count => this.zone.run(() => this.countSubject.next(count)));
  }

  private track(user: ChatUser): Observable<number> {
    const client = this.chat.chatClient;
    return new Observable<number>(subscriber => {
      let version = 0;
      if (typeof user.total_unread_count === 'number') {
        subscriber.next(user.total_unread_count);
      }

      const live = client.on(event => {
        if (typeof event.total_unread_count === 'number') {
          version++;
          subscriber.next(event.total_unread_count);
        }
      });

      const recovered = new Subject<void>();
      const onRecovered = client.on('connection.recovered', () =>
        recovered.next()
      );
      const visible = fromEvent(this.document, 'visibilitychange').pipe(
        filter(() => this.document.visibilityState === 'visible')
      );

      const resync = merge(recovered, visible)
        .pipe(
          startWith(undefined),
          switchMap(() => {
            const startedAt = version;
            return from(client.getUnreadCount()).pipe(
              map(res =>
                startedAt === version ? res.total_unread_count : null
              ),
              catchError(() => EMPTY)
            );
          }),
          filter((count): count is number => typeof count === 'number')
        )
        .subscribe(count => subscriber.next(count));

      return () => {
        live.unsubscribe();
        onRecovered.unsubscribe();
        resync.unsubscribe();
      };
    });
  }
}
